<template>
  <div class="donutChart">
    <div v-if="hasData" class="chartContain"  style="position: relative;">
      <div class="d-flex align-items-center justify-content-around chart-data-table">
        <div class="chart-wrapper">
          <svg class='chart' :viewBox='`0 0 ${chart.outerRadius*2} ${chart.outerRadius*2}`'
            preserveAspectRatio='xMidYMin slice' style="width: 100%;">
            <!-- 執行動畫的圓圈 -->
             <circle class='circle'
              v-for='(c, key) in donut'
              ref='circles'
              :key='`${key}${c.percentage}${c.offset}`'
              :r='radius'
              :cx='chart.outerRadius'
              :cy='chart.outerRadius'
              :stroke-width='chart.outerRadius - chart.innerRadius'
              :stroke-dashoffset='c.offset'
              :stroke='c.color'
              fill= 'transparent'/>
            <g
              class="arc"
              v-for="(p, key) in pie"
              :key="`${key}${p.d}`"
              :transform="`translate(${chart.outerRadius},${chart.outerRadius}) rotate(90)`"
              v-on:mouseover="showTooltip(key, $event)"
              v-on:mouseout="hiddenTooltip"
            >
              <path fill="transparent" :d="p.d"></path>
            </g>
            <text x="50%" y="50%" text-anchor="middle" v-show="!hideTooltip">
              <tspan
                x="50%"
                :fill="tooltipData.color"
                class="hover-text-title"
                font-size="32"
                :stroke="tooltipData.color"
                stroke-width="2px"
              >
                {{ tooltipData.value }}
              </tspan>
              <tspan x="50%" y="57%" stroke="#000" stroke-width="0" font-size="16">
                {{ tooltipData.name }}
              </tspan>
            </text>
          </svg>
        </div>
        <div class="chart-table-height">
          <b-table :items="donut" :fields="fields">
            <template #table-colgroup="scope">
              <col
                v-for="field in scope.fields"
                :key="field.key"
                :style="{ width: field.key === 'name' ? '300px' : '120px'  }"
              >
            </template>
            <template v-slot:cell(name)="{ item }">
              <div class="d-flex">
                <div class="border"
                :style="`background-color: ${item.color}`">&nbsp;</div>
                <div class="d-flex flex-column">
                  {{ item.name }}
                </div>
              </div>
            </template>
          </b-table>
        </div>
      </div>
      <div :class="{ tooltip: true, hidden: hideTooltip}">
        <div class="name"></div>
        <div class="value"></div>
      </div>
    </div>
    <div class="text-center">
      <NoDataState v-if="!hasData"></NoDataState>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3';
import NoDataState from '@/components/statistics/NoDataState.vue';
import {
  BTable,
} from 'bootstrap-vue';

export default {
  components: {
    NoDataState,
    BTable,
  },
  props: {
    chartdata: {
      type: Array,
    },
    thirdColumnName: {
      type: String,
      default: '數量',
    },
  },
  data() {
    return {
      chart: {
        innerRadius: 150, // 0 for PieChart
        outerRadius: 200,
      },
      hideTooltip: true,
      tooltipData: {

      },
      fields: [
        {
          key: 'name',
          sortable: false,
          label: ' ',
        },
        {
          key: 'percentage',
          sortable: false,
          label: '百份比',
          formatter: (value) => (Math.round(value).toString().concat('%')),
        },
        {
          key: 'value',
          sortable: false,
          label: this.thirdColumnName,
        },
      ],
    };
  },
  computed: {
    donut() {
      const newArray = [];
      let afterPer = 0; // 把前面的 percentage 累加 (0.xxx)
      if (this.chartdata.length) {
        this.chartdata.forEach((e, i) => {
          // 新增陣列
          newArray.push({
            percentage: (e.value / this.totalSum) * 100,
            offset: this.circum * (1 - afterPer),
            color: this.color(i),
            name: e.name,
            value: e.value,
          });
          // 把前面的 percentage 累加 (0.xxx)
          afterPer += (e.value / this.totalSum);
        });
      }
      return newArray;
    },
    // Circle 半徑
    radius() {
      return (
        this.chart.outerRadius
        - (this.chart.outerRadius - this.chart.innerRadius) / 2
      );
    },
    circum() {
      // circumference = 2 * pi * radius
      return 2 * Math.PI * this.radius;
    },
    color() {
      return d3.scaleOrdinal(d3.schemeDark2)
        .domain(this.chartdata.map((d) => d.name))
        .range(d3.quantize((t) => d3.interpolateSpectral(t * 0.8 + 0.1), this.chartdata.length)
          .reverse());
    },
    totalSum() {
      let sum = 0;
      if (this.chartdata.length) {
        this.chartdata.forEach((e) => {
          sum += e.value;
        });
      }
      return sum;
    },
    pie() {
      const newArray = [];
      const format = d3.format('.0%'); // 百分比單位
      const pie = d3
        .pie()
        .sort(null)
        .value((d) => d.value)(this.chartdata); // 準備好 arc 角度
      if (this.chartdata.length) {
        this.chartdata.forEach((e, i) => {
          // 準備好 arc 路徑
          const arc = d3
            .arc()
            .innerRadius(this.chart.innerRadius)
            .outerRadius(this.chart.outerRadius);
          // 新增陣列
          newArray.push({
            d: arc({
              startAngle: pie[i].startAngle,
              endAngle: pie[i].endAngle,
            }),
            centroid: arc.centroid(pie[i]),
            percentage: format(e.value / this.totalSum),
            count: e.value,
          });
        });
      }
      return newArray;
    },
    hasData() {
      return this.chartdata.some((data) => data.value > 0);
    },
  },
  mounted() {
    this.drawChart();
  },
  watch: {
    chartdata() {
      this.drawChart();
    },
  },
  methods: {
    drawChart() {
      // 用 js 跑展開動畫
      this.$nextTick().then(() => {
        // DOM updated
        this.donut.forEach((el, index) => {
          const totalTime = 100; // 設定時間跑 css
          const stroke = this.dasharray(el.percentage);
          // 起始位置
          this.$refs.circles[index].style.cssText = `stroke-dasharray: 0 ${
            this.circum
          }; opacity: 0`;
          // 設定 Interval
          setTimeout(() => {
            this.$refs.circles[index].style.cssText = `stroke-dasharray: ${
              stroke.dash
            } ${stroke.gap}; opacity: 1`;
          }, totalTime);
        });
      });
    },
    dasharray(percentage) {
      const dash = (this.circum / 100) * percentage; // percentage%
      const gap = (this.circum / 100) * (100 - percentage);
      return { dash, gap };
    },
    showTooltip(index, event) {
      const mouseX = event.clientX + 20;
      const mouseY = event.clientY;
      // 設置位置
      document
        .querySelector('.tooltip')
        .setAttribute('style', `left: ${mouseX}px; top: ${mouseY}px;`);
      // 插入名稱
      document.querySelector('.tooltip .name').innerHTML = `${
        this.chartdata[index].name
      } / ${this.pie[index].percentage}`;
      document.querySelector('.tooltip .value').innerHTML = `${
        this.chartdata[index].value
      } 件`;
      // 顯示 tooltip
      this.hideTooltip = false;
      this.setTooltipData(this.chartdata[index].name,
        this.pie[index].percentage,
        this.donut[index].color);
    },
    hiddenTooltip() {
      this.hideTooltip = true;
      this.tooltipData = {};
    },
    setTooltipData(name, percentage, color) {
      this.tooltipData.name = name;
      this.tooltipData.value = percentage;
      this.tooltipData.color = color;
    },
  },
};
</script>

<style lang="scss">
.chart-wrapper {
  width: 400px;
}
.donutChart {
  padding: 1rem;
  background-color: #fff;
  border: #dde1e5 1px solid;
  border-radius: 8px;
  /* 說明 */
  .detail {
    color: gray;
  }
  /* 統計圖 */
  .chartContain {
    position: relative;
    overflow: hidden;

    .chart {
      width: 400px;
      overflow: visible;
      .circle {
        transition: 1s;
      }

      .hover-text-title {
        text-shadow: 1px 1px #a9a9a9;
      }
    }
    ul.labelWrap {
      width: 100%;
      padding: 0;
      @media screen and (min-width: 600px) {
        width: 200px;
        height: 400px;
        padding-left: 50px;
        overflow: overlay;
      }
      li {
        list-style: none;
        display: block;
        margin: 15px 10px 0 0;
        .color {
          display: inline-block;
          width: 10px;
          height: 10px;
          margin: 5px 5px 0 0;
        }
        .percentage {
          color: rgba(0, 0, 0, 0.4);
        }
      }
    }
    .tooltip {
      min-width: 100px;
      background-color: rgba(0, 0, 0, 0.9);
      color: white;
      padding: 10px;
      border-radius: 6px;
      position: absolute;
      text-align: left;
      line-height: 1.5em;
      z-index: 1;
      &.hidden {
        visibility: hidden;
      }
    }
    .border {
      box-sizing: border-box;
      width: 15px;
      height: 15px;
      border-radius: 25%;
      margin-top: 6px;
    }
  }

  @include respond(phone) {
    .chart-data-table {
      flex-direction: column;
    }

    .chart-wrapper {
      width: 250px;
      padding-bottom: 10px;
    }
  }
}
  .chart-table-height {
    max-height: 400px;
    overflow: scroll;
  }

  @include respond(phone) {
    .chart-table-height {
      max-height: 300px;
    }
  }
</style>
